import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"
import { isoDate, jsonRecord } from "./primitives"

// ── ProductFacts ───────────────────────────────────────────────────────────

/** Major product family. Drives copy prompt selection and which attribute
 *  shape `buildAttributes` emits. Marketplace adapters map their hierarchy
 *  taxonomies to one of these values. */
export const categoryTypeSchema = z.enum(["clothing", "nutrition", "equipment"])
export type CategoryType = z.infer<typeof categoryTypeSchema>

/** Canonical membership test for the category-type enum. The single place a raw
 *  string is classified, so callers don't re-list the values (which then drift
 *  from the enum and from each other). */
export function isCategoryType(value: string): value is CategoryType {
  return (categoryTypeSchema.options as readonly string[]).includes(value)
}

export const productFactImageSchema = z.object({
  url: z.string().min(1),
  role: z.enum(["main", "gallery", "variant"]),
  position: z.number().int().nonnegative(),
})
export type ProductFactImage = z.infer<typeof productFactImageSchema>

export const productFactAttributeSchema = z.object({
  code: z.string().min(1),
  label: z.string().min(1),
  values: z.array(z.string()),
})
export type ProductFactAttribute = z.infer<typeof productFactAttributeSchema>

/** Omitted means uncaptured. Null source means captured absence; an empty
 * source value is an explicit EAV blank. Only the requested store or global wins. */
export const productWarningsSourceSchema = z
  .object({
    sku: z.string().min(1),
    magentoId: z.number().int().positive(),
    requestedStoreId: z.number().int().nonnegative(),
    source: z.object({ storeId: z.number().int().nonnegative(), value: z.string() }).nullable(),
  })
  .refine(
    (evidence) =>
      evidence.source === null ||
      evidence.source.storeId === 0 ||
      evidence.source.storeId === evidence.requestedStoreId,
    "Warnings must belong to the requested store or global scope"
  )
export type ProductWarningsSource = z.infer<typeof productWarningsSourceSchema>

export const productFactSimpleSchema = z.object({
  sku: z.string().min(1),
  warnings: productWarningsSourceSchema.optional(),
  magentoId: z.number().int().nullable(),
  name: z.string(),
  ean: z.string(),
  size: z.object({
    label: z.string(),
  }),
  color: z.object({
    label: z.string(),
  }),
  /** Regulatory ingredient statement from the catalog mirror. It stays
   *  per-simple because flavours under one configurable can differ. Writers
   *  copy this value verbatim; an empty string is a source gap. */
  ingredients: z.string(),
  /** Exact label inputs for marketplaces composing selected-variant descriptions.
   * Absence is incomplete evidence, never permission to borrow a sibling label. */
  labelSources: z
    .object({
      complete: z.boolean(),
      description: z.string(),
      shortDescription: z.string(),
      dosage: z.string(),
      directionsForUse: z.string(),
      marketingIngredients: z.string(),
      scopes: z.object({
        ingredients: z.enum(["selected_simple", "config_parent", "unavailable"]),
        dosage: z.enum(["selected_simple", "config_parent", "unavailable"]),
        directionsForUse: z.enum(["selected_simple", "config_parent", "unavailable"]),
        marketingIngredients: z.enum(["selected_simple", "config_parent", "unavailable"]),
      }),
    })
    .optional(),
  flavor: z
    .object({
      label: z.string(),
    })
    .optional(),
  image: z.string(),
  price: z.number().nullable(),
  finalPrice: z.number().nullable(),
  stockStatus: z.enum(["IN_STOCK", "OUT_OF_STOCK", ""]),
  weight: z.object({
    grossKg: z.number().nullable(),
    netKg: z.number().nullable(),
  }),
  /** VAT rate as a whole percent, from the mirror's `vat` column (resolved at
   *  sync time from `tax_class_id`). Marketplaces that require a tax-rate
   *  column on the PRODUCT feed — ePrice's required `VAT`, whose value list is
   *  exactly Italy's statutory rates — reach it as `fact:simple.vat`. The
   *  offer feed reads the same mirror column directly and does not go through
   *  facts. Null when the mirror has no rate for the row. */
  vat: z.number().nullable(),
})
export type ProductFactSimple = z.infer<typeof productFactSimpleSchema>

export const productFactsSchema = z.object({
  schemaVersion: z.literal("product_facts_v1"),
  source: z.object({
    system: z.literal("magento-sql"),
    storeCode: z.string().min(1),
    locale: z.string().min(1),
  }),
  marketplace: marketplaceIdSchema,
  config: z.object({
    warnings: productWarningsSourceSchema.optional(),
    labelComposition: z
      .object({ ingredients: z.string(), marketingIngredients: z.string() })
      .optional(),
    sku: z.string().min(1),
    type: z.enum(["configurable", "simple"]),
    name: z.object({
      localized: z.string(),
      english: z.string(),
    }),
    descriptions: z.object({
      shortText: z.string(),
      longHtml: z.string(),
    }),
    category: z.object({
      breadcrumb: z.string(),
      leaf: z.string(),
      paths: z.array(z.string()),
      /** Major product family derived from the marketplace's category taxonomy.
       *  Used to route copy prompts and pick which attribute extractor branch
       *  to emit. Defaults to "clothing" when the resolver cannot classify. */
      majorType: categoryTypeSchema,
    }),
    /** Marketplace-side category assigned to this SKU via marketplace_products_state
     *  (the slug / Mirakl hierarchy code, etc). Writers can read this via
     *  `fact:config.trackedCategory` to emit the category attribute instead
     *  of hardcoding it as a static — useful when one writer scope covers
     *  multiple leaf categories. */
    trackedCategory: z.string(),
    images: z.array(productFactImageSchema),
    ean: z.string(),
    brand: z.string(),
    countryOfManufacture: z.object({
      label: z.string(),
    }),
    gender: z.string(),
    color: z.object({
      label: z.string(),
    }),
    material: z.object({
      label: z.string(),
      components: z.array(
        z.object({
          label: z.string(),
          percentage: z.number().nullable(),
          percentageDecimal: z.string().nullable(),
        })
      ),
    }),
    attributes: z.array(productFactAttributeSchema),
    weight: z.object({
      grossKg: z.number().nullable(),
      netKg: z.number().nullable(),
    }),
    price: z.object({
      currency: z.string(),
      regular: z.number().nullable(),
      final: z.number().nullable(),
    }),
    status: z.object({
      stock: z.enum(["IN_STOCK", "OUT_OF_STOCK"]),
      enabled: z.boolean(),
      localBan: z.boolean(),
      longTermUnavailable: z.boolean(),
      delisted: z.boolean(),
    }),
  }),
  simples: z.array(productFactSimpleSchema),
})
export type ProductFacts = z.infer<typeof productFactsSchema>

// ── Materialized product values ────────────────────────────────────────────

export const productValueProvenanceSchema = z.enum([
  "catalog_derived",
  "ai_generated",
  "translated_from",
  "manual",
  "script",
])
export type ProductValueProvenance = z.infer<typeof productValueProvenanceSchema>

export const WRITER_TRACE_RULE_TYPES = [
  "ai_generate",
  "copy",
  "fact",
  "image_pick",
  "lookup",
  "nutrition_table",
  "optional",
  "static",
  "template",
  "transform",
] as const
export type WriterTraceRuleType = (typeof WRITER_TRACE_RULE_TYPES)[number]

const productValueWriterTraceStepSchema = z
  .object({
    attrCode: z.string(),
    simpleSku: z.string().nullable(),
    ruleType: z.enum(WRITER_TRACE_RULE_TYPES),
    value: z.string().nullable(),
    inputs: jsonRecord,
  })
  .strict()

const productValueRewriteSchema = z
  .object({
    stage: z.enum(["nutrition_image_reconciliation", "feed_transform"]),
    name: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
  })
  .strict()

const productValueDropSchema = z
  .object({
    simpleSku: z.string(),
    kind: z.enum(["row", "cell"]),
    reason: z.string(),
    value: z.string().optional(),
  })
  .strict()

const sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, "expected a lowercase sha256 hex digest")

/** Hash of the inputs a writer actually read to produce a value.
 *
 *  Generator-agnostic on purpose: the writer engine today, code fillers and
 *  agent copywriters after #233, all fill the same four slots. A consumer
 *  asserts freshness by recomputing this over its own resolved inputs and
 *  comparing `hash` — it never needs to know which writer ran, and **nothing
 *  gates on `writerVersion`**, which stays pure debugging provenance.
 *
 *  `version` is the component layout, not a data migration: an unrecognised
 *  version is stale by definition, never silently comparable. Bump it whenever a
 *  component is added, removed, or its inputs change. */
export const productValueInputFingerprintSchema = z
  .object({
    /** The component layout this row was stamped under.
     *
     *  Deliberately NOT `z.literal`. "Stale by definition" and "unparseable" are
     *  different outcomes: this schema is what `mapValue` runs on every
     *  `product_values` row, so a literal turns any layout but today's into a
     *  throw on `/products/:sku`, `GET /api/products/:sku/values`, the
     *  `SOURCE=db` harness and — after slice C — the build itself. Every future
     *  bump would be a fleet-wide read outage until re-materialization finished.
     *  The row must still parse; the freshness comparison
     *  (`version !== FINGERPRINT_VERSION` ⇒ stale) is the consumer's job. */
    version: z.number().int().positive(),
    /** Same reasoning as `version` — an unrecognised algorithm is stale, not
     *  unreadable. */
    algo: z.string().min(1),
    /** sha256 over the canonicalized `components` map — the one value consumers compare. */
    hash: sha256Hex,
    components: z
      .object({
        /** Catalog-mirror facts read for this config and its simples. Source
         *  copy (`description`, `short_description`) is included only when the
         *  generator reads it (`writerReadsSourceCopy`), so batch composition
         *  never moves the hash. Variants are sorted before hashing — the mirror
         *  loader returns them in heap order. */
        catalog: sha256Hex,
        /** The writer's ruleset identity — the active writer's compiled hash
         *  today, a filler/prompt version after #233. */
        generator: sha256Hex,
        /** Manual pins layered on top of the generated values. */
        overrides: sha256Hex,
        /** Marketplace runtime config + store the writer resolved against.
         *  Deliberately excludes the Mirakl/Octopia metadata cache: it is
         *  refetched wholesale, so its `fetchedAt` would invalidate every row
         *  nightly. A cache-driven divergence is caught by the CSV
         *  schema/value gate at build time instead. */
        marketplace: sha256Hex,
        /** External resolver payloads read (today: PIM nutrition tables),
         *  together with whether the generator consulted a resolver at all and
         *  whether the source answered. The prefetch is batch-scoped and
         *  fail-soft, so neither can be inferred from the payload: without the
         *  first, batch composition moves the hash; without the second, a
         *  degraded writer and a healthy consumer hash the same empty payload
         *  and the values read as fresh. */
        resolvers: sha256Hex,
      })
      .strict(),
  })
  .strict()
export type ProductValueInputFingerprint = z.infer<typeof productValueInputFingerprintSchema>

/** How a `filled_from` variant carries its fingerprint. Required for a writer,
 *  optional for a stored row — see {@link productValueWriteSchema} /
 *  {@link storedProductValueSchema}, which are the only two instantiations. */
type FingerprintField = z.ZodType<ProductValueInputFingerprint | undefined>

const canonicalWriterFilledFrom = <Fingerprint extends FingerprintField>(
  inputFingerprint: Fingerprint
) =>
  z
    .object({
      kind: z.literal("canonical_writer"),
      /** Debugging provenance only — never a freshness gate (#232 resolution 5).
       *
       *  A string since #315: writers are committed files with no version
       *  counter, so the writer stamps the writer's compiled hash. Rows written
       *  before that carry the old integer `effective_writers.version`, and the
       *  union is what lets them keep parsing — the alternative is a read
       *  outage on `/products/:sku` for every row not yet re-materialized. */
      writerIdentity: z.string().nullable(),
      category: z.string().nullable(),
      writerTrace: z.array(productValueWriterTraceStepSchema),
      rewrites: z.array(productValueRewriteSchema),
      drops: z.array(productValueDropSchema),
      inputFingerprint,
    })
    .strict()

export const canonicalWriterFilledFromSchema = canonicalWriterFilledFrom(
  productValueInputFingerprintSchema
)

// Historical provenance for rows written by the retired visual-image filler.
// Keep it readable because legacy product_values rows remain stored; new visual
// inputs live on immutable product-value generation revisions.
type ArtifactHashField = z.ZodType<string | null | undefined>

const visualImageProjectionFilledFrom = <ArtifactHash extends ArtifactHashField>(
  imageArtifactHash: ArtifactHash
) =>
  z
    .object({
      kind: z.literal("visual_image_projection"),
      /** The visual kind whose current image backs this value (registry kind). */
      visualKind: z.string(),
      /** The Magento store the image was rendered for. */
      store: z.string(),
      templateVersion: z.number().int(),
      /** Render-input fingerprint of the current image at projection time. */
      imageFingerprint: z.string(),
      /** Full SHA-256 of the encoded JPEG bytes. Null identifies legacy rows that
       * predate byte addressing; absent is tolerated only when reading a product
       * value written before this field existed. */
      imageArtifactHash,
      /** When the backing image row was created — the drift-check anchor. */
      imageCreatedAt: isoDate,
    })
    .strict()

export const visualImageProjectionFilledFromSchema = visualImageProjectionFilledFrom(
  sha256Hex.nullable()
)

const legacyOverrideFilledFrom = <Fingerprint extends FingerprintField>(
  inputFingerprint: Fingerprint
) =>
  z
    .object({
      kind: z.literal("legacy_override"),
      source: z.string(),
      value: z.string(),
      writtenAt: isoDate,
      /** Whether the pin currently contributes to a surviving final feed row.
       * Dropped pins remain visible because authored values win over derivation. */
      projectionStatus: z.enum(["active", "dropped"]),
      rewrites: z.array(productValueRewriteSchema),
      drops: z.array(productValueDropSchema),
      inputFingerprint,
    })
    .strict()

const productValueBaseSchema = z.object({
  attrCode: z.string(),
  simpleSku: z.string().nullable(),
  value: z.string(),
  updatedAt: isoDate,
  updatedBy: z.string(),
})

/** The value variants, over one `filled_from` fingerprint field shape. */
const productValueVariants = <
  Fingerprint extends FingerprintField,
  ArtifactHash extends ArtifactHashField,
>(
  inputFingerprint: Fingerprint,
  imageArtifactHash: ArtifactHash
) =>
  [
    productValueBaseSchema.extend({
      provenance: z.literal("catalog_derived"),
      filledFrom: z
        .object({
          kind: z.literal("catalog_facts"),
          factPaths: z.array(z.string()).min(1),
          inputFingerprint,
        })
        .strict(),
    }),
    productValueBaseSchema.extend({
      provenance: z.literal("ai_generated"),
      filledFrom: z
        .object({
          kind: z.literal("ai_generation"),
          model: z.string().min(1),
          inputs: jsonRecord,
          inputFingerprint,
        })
        .strict(),
    }),
    productValueBaseSchema.extend({
      provenance: z.literal("translated_from"),
      filledFrom: z
        .object({
          kind: z.literal("translated_value"),
          marketplace: marketplaceIdSchema,
          attrCode: z.string(),
          simpleSku: z.string().nullable(),
          inputFingerprint,
        })
        .strict(),
    }),
    productValueBaseSchema.extend({
      provenance: z.literal("manual"),
      filledFrom: legacyOverrideFilledFrom(inputFingerprint),
    }),
    productValueBaseSchema.extend({
      provenance: z.literal("script"),
      // The retired visual-image filler also used `script` provenance. Keep its
      // discriminant readable beside canonical writer history.
      filledFrom: z.discriminatedUnion("kind", [
        canonicalWriterFilledFrom(inputFingerprint),
        visualImageProjectionFilledFrom(imageArtifactHash),
      ]),
    }),
  ] as const

// The WRITE shape. Writer-style variants require `inputFingerprint`; the legacy
// visual variant requires `imageArtifactHash`. A writer that cannot identify what it read cannot be
// asserted against, so the type checker is the gate, not a convention in a doc.
// Every writer types its rows against this (`ProductValueWrite` in
// modules/product-values/repo.ts); nothing may widen a writer to the stored
// shape below, which is what the two names are for.
export const productValueWriteSchema = z.discriminatedUnion(
  "provenance",
  productValueVariants(productValueInputFingerprintSchema, sha256Hex.nullable())
)
export type ProductValueWriteShape = z.infer<typeof productValueWriteSchema>

// The STORED shape — what a READ of `product_values` may find, and the only
// shape a read boundary may parse with (`mapValue`, `productValuesDetailSchema`).
// `inputFingerprint` is OPTIONAL here because the table predates the field: the
// ~478k rows written before it exist, and the same reasoning that keeps
// `version` and `algo` open applies to absence. "Stale by definition" and
// "unparseable" are different outcomes, and an unparseable row takes the whole
// value out — `/products/:sku`, `GET /api/products/:sku/values`, the `SOURCE=db`
// harness, the slice-C build — instead of reporting it out of date. Every
// freshness consumer treats an absent fingerprint exactly like an unrecognised
// `version`: stale (`productValueStaleness` ⇒ `fingerprint_absent`), never
// fresh, never a throw.
export const storedProductValueSchema = z.discriminatedUnion(
  "provenance",
  productValueVariants(
    productValueInputFingerprintSchema.optional(),
    sha256Hex.nullable().optional()
  )
)
export type StoredProductValue = z.infer<typeof storedProductValueSchema>

export type CanonicalWriterFilledFrom = z.infer<typeof canonicalWriterFilledFromSchema>
export type VisualImageProjectionFilledFrom = z.infer<typeof visualImageProjectionFilledFromSchema>

const productValuesMarketplaceSchema = z.object({
  marketplace: marketplaceIdSchema,
  // A read response: the stored shape, fingerprint-optional.
  values: z.array(storedProductValueSchema),
})

const productValuesEligibilitySummarySchema = z.object({
  trackedMarketplaceCount: z.number().int().nonnegative(),
  eligibleMarketplaceCount: z.number().int().nonnegative(),
  unavailableMarketplaceCount: z.number().int().nonnegative(),
  exclusionReasons: z.array(
    z.object({
      reason: z.string().min(1),
      marketplaces: z.array(marketplaceIdSchema).min(1),
    })
  ),
})

// A known catalog/tracked product with no rows is an EMPTY read, not a failed
// read. The discriminant prevents the dashboard from mistaking a legitimate
// eligibility exclusion for a transport error, while an unknown SKU remains a
// route-level 404 and never enters this response shape.
export const productValuesDetailSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("materialized"),
    configSku: z.string(),
    totalValues: z.number().int().positive(),
    marketplaces: z.array(productValuesMarketplaceSchema).min(1),
  }),
  z.object({
    state: z.literal("empty"),
    configSku: z.string(),
    totalValues: z.literal(0),
    marketplaces: z.array(productValuesMarketplaceSchema).max(0),
    eligibility: productValuesEligibilitySummarySchema,
  }),
])
export type ProductValuesDetail = z.infer<typeof productValuesDetailSchema>
